<script setup lang="ts">
import type { ProgramItemDTO } from '~/shared/api/client/events/events.dto';
import { SpeakerCard } from '~/entities/speaker-card';
import { formatTime } from '../lib/formatTime';

const props = defineProps<{
  item: ProgramItemDTO;
  isOpen: boolean;
  isExpandable: boolean;
}>();

defineEmits<{
  toggle: [];
}>();

const timeRange = computed(() => {
  return `${formatTime(props.item.start_time)} — ${formatTime(props.item.end_time)}`;
});
</script>

<template>
  <div :class="$style.item">
    <div
      :class="[
        $style.row,
        { [$style.row_expandable!]: isExpandable, [$style.row_open!]: isOpen },
      ]"
      @click="isExpandable ? $emit('toggle') : undefined"
    >
      <div :class="$style.rowContent">
        <span :class="$style.time">{{ timeRange }}</span>
        <span :class="$style.name">{{ item.name }}</span>
      </div>
      <div v-if="isExpandable" :class="$style.toggle">
        <Icon
          v-if="!isOpen"
          name="my-icon:i-expand-arrow"
          :size="21"
          :class="$style.icon"
        />
        <Icon
          v-else
          name="my-icon:i-close-gray"
          :size="19"
          :class="$style.icon"
        />
      </div>
    </div>

    <div v-if="isOpen && isExpandable" :class="$style.expanded">
      <div v-if="item.description" :class="$style.description">
        {{ item.description }}
      </div>

      <template v-if="item.moderators.length > 0">
        <div :class="$style.sectionTitle">Модераторы</div>
        <div :class="[$style.cards, $style.cards_moderators]">
          <SpeakerCard
            v-for="speaker in item.moderators"
            :id="speaker.id"
            :key="speaker.id"
            :name="speaker.name"
            :position="speaker.position"
            :company="speaker.company?.name"
            :photo-url="speaker.photo?.file_url"
            :pending="item.pending_moderators?.includes(speaker.id)"
          />
        </div>
      </template>

      <template v-if="item.speakers.length > 0">
        <div :class="$style.sectionTitle">Спикеры</div>
        <div :class="[$style.cards, $style.cards_speakers]">
          <SpeakerCard
            v-for="speaker in item.speakers"
            :id="speaker.id"
            :key="speaker.id"
            :name="speaker.name"
            :position="speaker.position"
            :company="speaker.company?.name"
            :photo-url="speaker.photo?.file_url"
            :pending="item.pending_speakers?.includes(speaker.id)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style module lang="scss">
.item {
  border: 1px solid var(--color-gray-line);

  & + & {
    border-top: none;
  }
}

.row {
  display: flex;
  align-items: center;
  min-height: 86px;

  &_expandable {
    cursor: pointer;

    &:hover {
      background-color: var(--color-gray-bg);
    }
  }

  &_open {
    background-color: var(--color-gray-bg);
  }
}

.rowContent {
  flex: 1;
  display: flex;
  gap: var(--space-1-5x);
  align-items: flex-start;
  padding: 20px;
  min-width: 0;
}

.time {
  @include text_body2;
  color: var(--color-black-1);
  width: 115px;
  flex-shrink: 0;
}

.name {
  font-family: var(--font-golos, 'Golos Text', sans-serif);
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;
  color: var(--color-black-1);
}

.toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 86px;
  min-height: 86px;
  align-self: stretch;
  flex-shrink: 0;
  border-left: 1px solid var(--color-gray-line);
}

.icon {
  color: #757575;
}

.expanded {
  background-color: var(--color-gray-bg);
}

.sectionTitle {
  @include text_body1_medium;
  color: var(--color-black-1);
  padding: 14px 20px;
  border-top: 1px solid var(--color-gray-line);
  border-bottom: 1px solid var(--color-gray-line);
}

.cards {
  display: grid;
  gap: 1px;
  background-color: var(--color-gray-line);

  &_moderators {
    grid-template-columns: repeat(2, 1fr);
  }

  &_speakers {
    grid-template-columns: repeat(3, 1fr);
  }
}

.description {
  @include text_body1;
  color: var(--color-black-1);
  padding: 20px;
  border-top: 1px solid var(--color-gray-line);
  white-space: pre-line;
}

@media (max-width: 1379px) {
  .item {
    border-left: none;
    border-right: none;
  }

  .cards_speakers {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  .rowContent {
    flex-direction: column;
    gap: var(--space-0-5x);
  }

  .time {
    width: auto;
  }
}

@media (max-width: 599px) {
  .cards_moderators,
  .cards_speakers {
    grid-template-columns: 1fr;
  }

  .name,
  .description {
    font-size: 14px;
  }
}
</style>
